import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ContextMenu } from '~/components/Atoms/ContextMenu'

// Flag SVG imports
import CSFlag from '~/assets/flags/cs.svg?react'
import DEFlag from '~/assets/flags/de.svg?react'
import ENFlag from '~/assets/flags/en.svg?react'
import ESFlag from '~/assets/flags/es.svg?react'
import FRFlag from '~/assets/flags/fr.svg?react'
import HUFlag from '~/assets/flags/hu.svg?react'
import ITFlag from '~/assets/flags/it.svg?react'
import NLFlag from '~/assets/flags/nl.svg?react'
import PTFlag from '~/assets/flags/pt.svg?react'
import ROFlag from '~/assets/flags/ro.svg?react'

export function LanguageSelector() {
  const [open, setOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const theme = useTheme()
  const { i18n } = useTranslation()

  // Flag wrapper to avoid style duplication and apply circular clipping via CSS
  const FlagWrapper = ({ children }: { children: React.ReactNode }) => (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  )

  const LANGUAGES = [
    {
      code: 'en',
      name: 'English',
      flag: () => <ENFlag />,
    },
    {
      code: 'fr',
      name: 'Français',
      flag: () => <FRFlag />,
    },
    {
      code: 'de',
      name: 'Deutsch',
      flag: () => <DEFlag />,
    },
    {
      code: 'es',
      name: 'Español',
      flag: () => <ESFlag />,
    },
    {
      code: 'it',
      name: 'Italiano',
      flag: () => <ITFlag />,
    },
    {
      code: 'pt',
      name: 'Português',
      flag: () => <PTFlag />,
    },
    {
      code: 'nl',
      name: 'Nederlands',
      flag: () => <NLFlag />,
    },
    {
      code: 'cs',
      name: 'Čeština',
      flag: () => <CSFlag />,
    },
    {
      code: 'hu',
      name: 'Magyar',
      flag: () => <HUFlag />,
    },
    {
      code: 'ro',
      name: 'Română',
      flag: () => <ROFlag />,
    },
  ]

  const currentLanguageCode = (i18n.resolvedLanguage || i18n.language || '').split('-')[0]
  const currentLanguage = LANGUAGES.find((lang) => lang.code === currentLanguageCode) || LANGUAGES[0]

  const handleLanguageChange = async (languageCode: string) => {
    try {
      await i18n.changeLanguage(languageCode)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to change language', error)
    } finally {
      setOpen(false)
    }
  }

  return (
    <>
      <ButtonBase
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          setAnchorEl(event.currentTarget)
          setOpen((prevOpen) => !prevOpen)
        }}
        sx={{
          border: `0.5px solid ${theme.palette.border.main}`,
          borderRadius: theme.radius.full,
          padding: `${theme.spacing.S} ${theme.spacing.M}`,
          '&:hover': {
            backgroundColor: theme.palette.primary.lightest,
          },
        }}
      >
        <Box display="flex" alignItems="center" gap={theme.spacing.S}>
          <FlagWrapper>{currentLanguage.flag()}</FlagWrapper>
          <KeyboardArrowDownIcon />
        </Box>
      </ButtonBase>

      <ContextMenu open={open} onClose={() => setOpen(false)} anchorEl={anchorEl}>
        {LANGUAGES.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            selected={language.code === i18n.language}
            sx={{
              minWidth: '160px',
              borderRadius: theme.radius.M,
              marginTop: theme.spacing.S,
              padding: `${theme.spacing.M} ${theme.spacing.L}`,
              color: theme.palette.text.primary,
              '&.Mui-selected': {
                backgroundColor: theme.palette.primary.selected,
                color: theme.palette.common.white,
              },
              '&:hover': {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.primary.hover,
              },
            }}
          >
            <Box display="flex" alignItems="center" gap={theme.spacing.L} sx={{ minHeight: 20 }}>
              <FlagWrapper>
                <language.flag />
              </FlagWrapper>
              <Typography variant="body">{language.name}</Typography>
            </Box>
          </MenuItem>
        ))}
      </ContextMenu>
    </>
  )
}
